import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { 
  Search, Grid3X3, Code, Mail, FileText, Share2, Lock, Crown,
  Terminal, FileJson, Hash, Key, Fingerprint, Braces, Type, AlignLeft, MessageSquare, 
  AtSign, Files, Scissors, FileUser, Image, Clock, Twitter, Webhook,
  Sparkles, Wand2, RefreshCw, Bot, FileSearch, Calendar,
  QrCode, Palette, Camera, Smile, ImageIcon, FileCode, History,
  Maximize, Crop, Ruler, FileSpreadsheet, Timer,
  Gauge, Droplets, Eraser, RotateCw, ScanLine, BarChart3, Shuffle, Hourglass, Wrench
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { useProfile } from '@/hooks/useProfile';
import { cn } from '@/lib/utils';
import NewBadge from '@/components/NewBadge';

interface Tool {
  name: string;
  description: string;
  path: string;
  icon: React.ElementType;
  category: 'ai' | 'developer' | 'email' | 'document' | 'pdf' | 'social' | 'design' | 'utilities' | 'pro';
  isNew?: boolean;
  isPro?: boolean;
  isImproved?: boolean;
}

const tools: Tool[] = [
  // AI Tools
  { name: 'AI Humanizer', description: 'Humanize AI-generated text naturally', path: '/dashboard/tools/ai-humanizer', icon: Wand2, category: 'ai', isNew: true },
  { name: 'Prompt Generator', description: 'Create optimized AI prompts', path: '/dashboard/tools/prompt-generator', icon: Sparkles, category: 'ai', isNew: true },
  { name: 'Paraphraser', description: 'Rewrite text in different styles', path: '/dashboard/tools/paraphraser', icon: RefreshCw, category: 'ai', isNew: true },
  { name: 'Plagiarism Checker', description: 'Check text for plagiarism', path: '/dashboard/tools/plagiarism-checker', icon: Search, category: 'ai', isNew: true },
  { name: 'AI Detector', description: 'Detect AI-generated content', path: '/dashboard/tools/ai-detector', icon: Bot, category: 'ai', isNew: true },

  // Developer Tools
  { name: 'Code Studio', description: 'Live HTML/CSS/JS editor with preview — New & Improved!', path: '/dashboard/tools/code-studio', icon: Terminal, category: 'developer', isImproved: true },
  { name: 'JSON Formatter', description: 'Format and validate JSON data', path: '/dashboard/tools/json-formatter', icon: FileJson, category: 'developer' },
  { name: 'Base64 Tool', description: 'Encode and decode Base64 strings', path: '/dashboard/tools/base64', icon: Braces, category: 'developer' },
  { name: 'Hash Generator', description: 'Generate MD5, SHA-1, SHA-256 hashes', path: '/dashboard/tools/hash-generator', icon: Hash, category: 'developer' },
  { name: 'Password Generator', description: 'Create secure random passwords', path: '/dashboard/tools/password-generator', icon: Key, category: 'developer' },
  { name: 'UUID Generator', description: 'Generate unique identifiers', path: '/dashboard/tools/uuid-generator', icon: Fingerprint, category: 'developer' },
  { name: 'CSV ↔ JSON', description: 'Convert between CSV and JSON formats', path: '/dashboard/tools/csv-json', icon: FileSpreadsheet, category: 'developer', isNew: true },
  { name: 'Timestamp Converter', description: 'Convert Unix timestamps to dates', path: '/dashboard/tools/timestamp-converter', icon: Timer, category: 'developer', isNew: true },
  
  // Email Tools
  { name: 'Temp Mail', description: 'Generate temporary email addresses', path: '/dashboard/tools/temp-mail', icon: AtSign, category: 'email', isNew: true },
  { name: 'Mail History', description: 'View past temp mail sessions', path: '/dashboard/tools/temp-mail/history', icon: History, category: 'email' },
  
  // Document Tools
  { name: 'Days Calculator', description: 'Calculate days between dates', path: '/dashboard/tools/days-calculator', icon: Calendar, category: 'document', isNew: true },
  { name: 'Word Counter', description: 'Count words, characters, sentences', path: '/dashboard/tools/word-counter', icon: FileCode, category: 'document', isNew: true },
  { name: 'Lorem Ipsum', description: 'Generate placeholder text', path: '/dashboard/tools/lorem-ipsum', icon: Type, category: 'document', isNew: true },
  { name: 'Text Case Converter', description: 'Convert text between cases', path: '/dashboard/tools/text-case', icon: AlignLeft, category: 'document', isNew: true },
  { name: 'Markdown to HTML', description: 'Convert Markdown to HTML', path: '/dashboard/tools/markdown-html', icon: FileText, category: 'document', isNew: true },
  { name: 'Unit Converter', description: 'Convert length, weight, temperature, time', path: '/dashboard/tools/unit-converter', icon: Ruler, category: 'document', isNew: true },

  // PDF Tools
  { name: 'PDF Analyzer', description: 'AI-powered PDF analysis', path: '/dashboard/tools/pdf-analyzer', icon: FileSearch, category: 'pdf', isNew: true },
  { name: 'PDF Editor', description: 'Edit, annotate, and reorder PDF pages', path: '/dashboard/tools/pdf-editor', icon: FileText, category: 'pdf', isNew: true },
  { name: 'PDF Compressor', description: 'Reduce PDF file size', path: '/dashboard/tools/pdf-compressor', icon: Gauge, category: 'pdf', isNew: true },
  { name: 'PDF Password', description: 'Add or remove PDF password protection', path: '/dashboard/tools/pdf-password', icon: Lock, category: 'pdf', isNew: true },
  { name: 'PDF Watermark', description: 'Add text watermarks to PDF pages', path: '/dashboard/tools/pdf-watermark', icon: Droplets, category: 'pdf', isNew: true },
  { name: 'PDF Merge', description: 'Combine multiple PDFs into one', path: '/dashboard/tools/pdf-merge', icon: Files, category: 'pdf', isNew: true },
  { name: 'PDF Splitter', description: 'Split PDF by page ranges', path: '/dashboard/tools/pdf-splitter', icon: Scissors, category: 'pdf', isNew: true },
  { name: 'PDF to Image', description: 'Convert PDF pages to images', path: '/dashboard/tools/pdf-to-image', icon: Image, category: 'pdf', isNew: true },
  { name: 'Resume Builder', description: 'Create professional resumes', path: '/dashboard/tools/resume-builder', icon: FileUser, category: 'pdf', isNew: true },
  
  // Social Tools
  { name: 'Discord Timestamp', description: 'Create Discord timestamps', path: '/dashboard/tools/discord-timestamp', icon: Clock, category: 'social', isNew: true },
  { name: 'Twitter Counter', description: 'Count characters for tweets', path: '/dashboard/tools/twitter-counter', icon: Twitter, category: 'social', isNew: true },
  { name: 'Discord Embed Builder', description: 'Create Discord webhook embeds', path: '/dashboard/tools/discord-embed', icon: MessageSquare, category: 'social', isNew: true },
  { name: 'Webhook Sender', description: 'Send webhook requests', path: '/dashboard/tools/webhook-sender', icon: Webhook, category: 'social', isNew: true },

  // Design Tools
  { name: 'QR Code Generator', description: 'Generate QR codes instantly', path: '/dashboard/tools/qr-code', icon: QrCode, category: 'design', isNew: true },
  { name: 'Image Compressor', description: 'Compress images without quality loss', path: '/dashboard/tools/image-compressor', icon: ImageIcon, category: 'design', isNew: true },
  { name: 'Image Resizer', description: 'Resize images to any dimension', path: '/dashboard/tools/image-resizer', icon: Maximize, category: 'design', isNew: true },
  { name: 'Image Cropper', description: 'Crop images with custom aspect ratios', path: '/dashboard/tools/image-cropper', icon: Crop, category: 'design', isNew: true },
  { name: 'Image Converter', description: 'Convert between PNG, JPG, WebP', path: '/dashboard/tools/image-converter', icon: RefreshCw, category: 'design', isNew: true },
  { name: 'Background Remover', description: 'Remove image backgrounds', path: '/dashboard/tools/background-remover', icon: Eraser, category: 'design', isNew: true },
  { name: 'Image Rotate & Flip', description: 'Rotate and flip images', path: '/dashboard/tools/image-rotate-flip', icon: RotateCw, category: 'design', isNew: true },
  { name: 'Placeholder Image', description: 'Generate placeholder images', path: '/dashboard/tools/placeholder-image', icon: ImageIcon, category: 'design', isNew: true },
  { name: 'Color Picker', description: 'Pick and convert colors', path: '/dashboard/tools/color-picker', icon: Palette, category: 'design', isNew: true },
  { name: 'Favicon Generator', description: 'Create favicons from images', path: '/dashboard/tools/favicon-generator', icon: Image, category: 'design', isNew: true },
  { name: 'Screenshot Beautifier', description: 'Beautify screenshots with backgrounds', path: '/dashboard/tools/screenshot-beautifier', icon: Camera, category: 'design', isNew: true },
  { name: 'Emoji Picker', description: 'Browse and copy emojis', path: '/dashboard/tools/emoji-picker', icon: Smile, category: 'design', isNew: true },
  
  // PDF Extra
  { name: 'Office to PDF', description: 'Convert Office documents to PDF', path: '/dashboard/tools/office-to-pdf', icon: FileText, category: 'pdf', isNew: true },

  // Utilities
  { name: 'QR Code Reader', description: 'Decode QR codes from images', path: '/dashboard/tools/qr-reader', icon: ScanLine, category: 'utilities', isNew: true },
  { name: 'Barcode Generator', description: 'Generate barcodes in various formats', path: '/dashboard/tools/barcode-generator', icon: BarChart3, category: 'utilities', isNew: true },
  { name: 'Random Picker', description: 'Randomly pick items from a list', path: '/dashboard/tools/random-picker', icon: Shuffle, category: 'utilities', isNew: true },
  { name: 'Countdown Timer', description: 'Live countdown to any date', path: '/dashboard/tools/countdown-timer', icon: Hourglass, category: 'utilities', isNew: true },
  { name: 'Pomodoro Timer', description: 'Stay focused with timed work sessions', path: '/dashboard/tools/pomodoro-timer', icon: Clock, category: 'utilities', isNew: true },
  
  // Pro Tools
  { name: 'BIN Generator', description: 'Generate card numbers from BIN', path: '/dashboard/pro-tools', icon: Crown, category: 'pro', isPro: true },
  { name: 'Card Validator', description: 'Validate card numbers using Luhn', path: '/dashboard/pro-tools', icon: Crown, category: 'pro', isPro: true },
  { name: 'BIN Lookup', description: 'Lookup BIN information', path: '/dashboard/pro-tools', icon: Crown, category: 'pro', isPro: true },
];

const categories = [
  { id: 'all', label: 'All Tools', icon: Grid3X3 },
  { id: 'ai', label: 'AI', icon: Sparkles },
  { id: 'developer', label: 'Developer', icon: Code },
  { id: 'email', label: 'Email', icon: Mail },
  { id: 'document', label: 'Document', icon: FileText },
  { id: 'pdf', label: 'PDF', icon: Files },
  { id: 'social', label: 'Social', icon: Share2 },
  { id: 'design', label: 'Design', icon: Palette },
  { id: 'utilities', label: 'Utilities', icon: Wrench },
  { id: 'pro', label: 'Pro Tools', icon: Crown },
];

const AllTools = () => {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const { isPremium } = useProfile();

  const filteredTools = useMemo(() => {
    return tools.filter(tool => {
      const matchesSearch = tool.name.toLowerCase().includes(search.toLowerCase()) ||
        tool.description.toLowerCase().includes(search.toLowerCase());
      const matchesCategory = activeCategory === 'all' || tool.category === activeCategory;
      return matchesSearch && matchesCategory;
    });
  }, [search, activeCategory]);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-xl bg-primary/20">
          <Grid3X3 className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">All Tools</h1>
          <p className="text-muted-foreground">
            Browse and access all {tools.length} available tools
          </p>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search tools..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-background/50"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {categories.map(cat => (
            <Button
              key={cat.id}
              variant={activeCategory === cat.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveCategory(cat.id)}
              className="gap-2"
            >
              <cat.icon className="h-4 w-4" />
              <span className="hidden sm:inline">{cat.label}</span>
            </Button>
          ))}
        </div>
      </div>

      {/* Tools Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filteredTools.map((tool) => {
          const isLocked = tool.isPro && !isPremium;
          
          return (
            <Link
              key={tool.path + tool.name}
              to={isLocked ? '#' : tool.path}
              onClick={(e) => isLocked && e.preventDefault()}
            >
              <Card className={cn(
                "p-4 glass border-border/50 transition-all duration-300 h-full group",
                "hover:border-primary/70 hover:shadow-lg hover:shadow-primary/10 hover:-translate-y-1 hover:bg-accent/50",
                isLocked && "opacity-60 cursor-not-allowed hover:translate-y-0 hover:shadow-none"
              )}>
                <div className="flex items-start gap-3">
                  <div className={cn(
                    "p-2.5 rounded-lg shrink-0 transition-all duration-300",
                    tool.isPro ? "bg-warning/20 group-hover:bg-warning/30 group-hover:scale-110" : "bg-primary/20 group-hover:bg-primary/30 group-hover:scale-110"
                  )}>
                    <tool.icon className={cn(
                      "h-5 w-5 transition-colors duration-300",
                      tool.isPro ? "text-warning" : "text-primary"
                    )} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-sm truncate group-hover:text-primary transition-colors duration-300">
                        {tool.name}
                      </h3>
                      {tool.isNew && <NewBadge />}
                      {tool.isImproved && (
                        <Badge className="bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white text-[10px] px-1.5 py-0 border-0 whitespace-nowrap">
                          ✨ New & Improved
                        </Badge>
                      )}
                      {tool.isPro && (
                        <Badge className="bg-gradient-to-r from-yellow-500 to-amber-500 text-white text-[10px] px-1.5 py-0 border-0">
                          PRO
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2 group-hover:text-foreground/70 transition-colors duration-300">
                      {tool.description}
                    </p>
                  </div>
                  {isLocked && <Lock className="h-4 w-4 text-muted-foreground shrink-0" />}
                </div>
              </Card>
            </Link>
          );
        })}
      </div>

      {filteredTools.length === 0 && (
        <div className="text-center py-12">
          <p className="text-muted-foreground">No tools found matching your search.</p>
        </div>
      )}
    </div>
  );
};

export default AllTools;
